import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { APP_VERSION } from '../utils/constants'
import './Auth.css'

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    otp: ''
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [sendingOTP, setSendingOTP] = useState(false)
  const { register, generateOTP, loginWithOTP } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'mobile') {
      const sanitized = value.replace(/\D/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: sanitized
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    setError('')
  }

  const handleSendOTP = async () => {
    setError('')
    if (!formData.mobile.trim() || formData.mobile.length !== 10) {
      setError('Please enter a valid 10-digit mobile number')
      return
    }

    setSendingOTP(true)
    try {
      const result = await generateOTP(formData.mobile)
      if (result.success) {
        setOtpSent(true)
        if (result.data?.otp && process.env.NODE_ENV === 'development') {
          setError(`OTP sent! (Dev - OTP: ${result.data.otp})`)
        } else {
          setError('OTP sent to your mobile number!')
          setTimeout(() => setError(''), 3000)
        }
      } else {
        setError(result.message || 'Failed to send OTP')
      }
    } catch (error) {
      setError('An inesperado error occurred.')
    } finally {
      setSendingOTP(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    const { name, email, mobile, otp } = formData;

    if (!name.trim()) {
      setError('Please enter your full name')
      return
    }

    if (!mobile.trim()) {
      setError('Please enter your mobile number')
      return
    }

    if (!otpSent) {
      await handleSendOTP()
      return
    }

    if (!otp.trim()) {
      setError('Please enter the OTP')
      return
    }

    setLoading(true)
    try {
      // Use the register function which now accepts OTP
      // Pass null for password as we are using OTP based registration
      const result = await register(name, email, null, mobile, otp)
      
      if (result.success) {
        navigate('/profile')
      } else {
        setError(result.message || 'Registration failed. Please try again.')
      }
    } catch (err) {
      setError('An error occurred during registration.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-logo-container">
            <div className="logo-text-v2 auth-logo-adjust">
              <span className="logo-part-nah">NAH</span>
              <span className="logo-part-44">44</span>
            </div>
          </div>
          <h2>Create Account</h2>
          <p className="auth-subtitle">Join NAH44 and experience secure vehicle management.</p>

          {error && (
            <div className={`auth-alert ${error.includes('OTP sent') ? 'alert-success' : 'alert-danger'}`}>
              <span>{error.includes('OTP sent') ? '✅' : '⚠️'}</span>
              <span>{error}</span>
            </div>
          )}

          {loading && !error && (
            <div className="auth-alert alert-loading">
              <div className="auth-spinner"></div>
              <span>Creating your account...</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <div className="input-container">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  disabled={loading || otpSent}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address (Optional)</label>
              <div className="input-container">
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="name@example.com"
                  disabled={loading || otpSent}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="mobile">Mobile Number</label>
              <div className="input-container mobile-input-wrapper">
                <span className="country-code">+91</span>
                <input
                  type="tel"
                  id="mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="9999999999"
                  disabled={loading || otpSent}
                />
              </div>
            </div>

            {otpSent && (
              <div className="form-group slide-up">
                <label htmlFor="otp">Enter 6-Digit OTP</label>
                <div className="input-container">
                  <input
                    type="text"
                    id="otp"
                    name="otp"
                    className="otp-input"
                    value={formData.otp}
                    onChange={handleChange}
                    placeholder="000000"
                    maxLength="6"
                    disabled={loading}
                  />
                </div>
                <div className="resend-box">
                  <button
                    type="button"
                    className="btn-resend"
                    onClick={handleSendOTP}
                    disabled={sendingOTP || loading}
                  >
                    Resend Code
                  </button>
                </div>
              </div>
            )}

            <div className="auth-actions">
              <button
                type="submit"
                className="btn-auth"
                disabled={loading || sendingOTP}
              >
                {loading ? (
                  <div className="auth-spinner"></div>
                ) : (
                  <span>{otpSent ? 'Verify & Register' : 'Send OTP'}</span>
                )}
              </button>
            </div>
          </form>

          <div className="auth-footer">
            Already have an account? <Link to="/login">Sign In</Link>
          </div>
          
          <div style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: 'var(--text-secondary)',
            opacity: 0.5,
            marginTop: '1rem'
          }}>
            v{APP_VERSION}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Register
